import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import moment from 'moment';
import useStarfishAPI from "../hooks/useStarfishAPI";
import InfoMessage from "../common/InfoMessage";
import ErrorMessage from "../common/ErrorMessage";

/**
 * System announcement for staff or students (defaults to students).
 *
 * @param {boolean} studentAnnouncementEnabled - Config that enables the separate student announcement
 * @param {boolean} [forStaff=false] - Indicates whether or not this is an announcement for staff
 * @returns {React.Component} A react component containing a styled Announcement
 */
const Announcement = ({forStaff, studentAnnouncementEnabled}) => {
  const useStudentAnnouncement = studentAnnouncementEnabled && !forStaff
  const URL = useStudentAnnouncement ? `../api/announcements?type=student` : `../sysAnnouncement.html`;

  const [announcement, setAnnouncement] = useState("");
  const [response, callAPI] = useStarfishAPI({ isNewAPI: useStudentAnnouncement });
  const [error, setError] = useState(null);

  const fetchAnnouncement = useCallback(() => {
    callAPI(URL, {
      method: "GET",
      headers: {
        Pragma: "no-cache",
        "Cache-Control": "no-cache",
        Expires: "-1"
      }
    });
  }, []);

  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  useEffect(() => {
    if (response.error)
      setError("We're having trouble displaying announcements right now. " +
        "If you see this message again, please " +
        "contact your institution's technical support.")
    ;
    if (!response.success) return;

    // TODO Remove this if block when use of System Announcement Tenant Config is deprecated
    if (!useStudentAnnouncement) {
      setAnnouncement(response.success
          && !isEmpty(response.data.announcements)
          && response.data.announcements[0].sysAnn);
      return;
    }

    const firstAnnouncement = !isEmpty(response.data) && response.data[0].announcements[0]
    if (!firstAnnouncement
        || moment(firstAnnouncement.startDate).isAfter(moment(), 'day')
        || moment(firstAnnouncement.endDate).isBefore(moment(), 'day')) return;
    setAnnouncement(firstAnnouncement.message);
  }, [response, forStaff, setAnnouncement]);

  if (error) return <ErrorMessage message={error}/>;
  if ( !announcement ) return null;

  return <InfoMessage message={announcement} icon='info' isHtml isCollapsable />;
};

Announcement.propTypes = {
  forStaff: PropTypes.bool,
  studentAnnouncementEnabled: PropTypes.bool.isRequired,
};

export default Announcement;
